import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import cl from 'classnames';
import debounce from 'lodash/debounce';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';

import { IconsEnum } from '../../../../../../../assets/icons/types';

import { useTranslate } from '../../../../../../../common/hooks/useTranslate';

import { Icon } from '../../../../../../../helpers/Icon';

import { words } from '../../../../../../../locales/keys';

interface ThreeDStockProductsFiltersSearchByNameFilterProps {
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
  isCompact?: boolean;
}

const nameFieldName = FetchProductsFilterFields.NAME;

const wait = 500;

function ThreeDStockProductsFiltersSearchByNameFilter({
  productsFilters,
  changeProductsFilters,
  isCompact
}: ThreeDStockProductsFiltersSearchByNameFilterProps) {
  const { t } = useTranslate();

  const filterValue = productsFilters?.[nameFieldName]?.ilike || '';

  const [value, setValue] = useState<string>(filterValue);

  const debouncedChangeFilter = useMemo(
    () =>
      debounce((input: string) => {
        if (input) {
          changeProductsFilters({ [nameFieldName]: { ilike: input } });
        } else {
          changeProductsFilters({}, [nameFieldName]);
        }
      }, wait),
    [changeProductsFilters]
  );

  useEffect(
    () => () => debouncedChangeFilter.cancel(),
    [debouncedChangeFilter]
  );

  const handleChange = useCallback<(e: ChangeEvent<HTMLInputElement>) => void>(
    (e) => {
      const input = e?.target?.value || '';
      setValue(input);
      debouncedChangeFilter(input);
    },
    [debouncedChangeFilter]
  );

  // The name filter can be removed outside this input (applied-filters
  // "Clear filters"); the pending debounced apply must not re-add it.
  useEffect(() => {
    if (!filterValue) {
      debouncedChangeFilter.cancel();
      setValue((prevValue) => (prevValue ? '' : prevValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  return (
    <div className={cl(isCompact ? 'p-2' : 'p-4')}>
      <div className="relative">
        <div className="absolute flex inset-y-0 items-center left-0 ml-3 pointer-events-none text-gray-500 dark:text-gray-400">
          <Icon icon={IconsEnum.SEARCH} className="h-4 w-4" />
        </div>
        <input
          data-ga="products-filters-search-by-name-input"
          type="text"
          value={value}
          placeholder={t(words.searchByName)}
          className={cl(
            'basic-input pl-9',
            isCompact ? 'text-xs sm:text-xs' : 'text-sm'
          )}
          onChange={handleChange}
        />
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersSearchByNameFilter;
